/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, formatDate, DatePipe } from "@angular/common";
import { AfterViewInit, Component, DestroyRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, filter, first, interval, map, Observable, of,
    ReplaySubject, shareReplay, startWith, Subject, switchMap, tap } from "rxjs";
import {
    isInDestroyableState,
    isInResumableState,
    isInPausableState,
    Server,
    Cluster,
    ClusterStatus,
    isInConnectableState, Snapshot, ClusterAutoPauseInformation
} from "../../../concept/cluster";
import { hasAdminAccess, hasWriteAccess } from "../../../concept/iam";
import { LOADING } from "../../../framework/strings";
import { DIALOG, SETUP } from "../../../framework/url-params";
import { clustersPath, memberDetailsPath, serverDetailsPath, teamSettingsPath } from "../../../routing/resource-paths";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import {
    ButtonComponent, ConfirmationModalComponent, ConfirmationModalData, DatetimePipe,
    SpinnerComponent,
    StrongConfirmationModalComponent, StrongConfirmationModalData,
    patternValidator, requiredValidator
} from "typedb-cloud-framework";
import { DialogResult } from "../../../service/dialog.service";

import { SpaceApi } from "../../../service/space/space-api.service";
import { ApplicationState } from "../../../service/application-state.service";
import { ResourceService } from "../../../service/resource.service";
import { SnackbarService } from "../../../service/snackbar.service";
import { generateUniqueId, isBlank, toSentenceCase } from "../../../util";
import { PageScaffoldComponent, ResourceAvailability } from "../../scaffold/page/page-scaffold.component";
import { ClusterStatusPipe } from "../cluster-status.pipe";
import { MachineTypePipe } from "../machine-type.pipe";
import { RegionPipe } from "../region.pipe";
import { StorageSizePipe } from "../storage-size.pipe";
import { ClusterEditDialogData, ClusterUpdateDialogComponent } from "../update/cluster-update-dialog.component";
import { ServerStatusPipe } from "../server-status.pipe";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatDialog } from "@angular/material/dialog";
import { TableCopyableTextCellComponent } from "../../../framework/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { PauseResumeModalComponent, PauseResumeModalData } from "../pause-resume/pause-resume-modal.component";
import { CloneClusterModalComponent, CloneClusterModalData } from "../clone/clone-cluster-modal.component";
import { MatExpansionModule } from "@angular/material/expansion";
import { ClusterConsoleConnectDialogComponent } from "./connect/console/cluster-console-connect-dialog.component";
import { ClusterDriversConnectDialogComponent } from "./connect/drivers/cluster-drivers-connect-dialog.component";
import { ClusterHttpConnectDialogComponent } from "./connect/http/cluster-http-connect-dialog.component";
import { ClusterStudioConnectDialogComponent } from "./connect/studio/cluster-studio-connect-dialog.component";
import { ClusterSetupDialogComponent } from "./setup/cluster-setup-dialog.component";
import {
    ClusterResetUserPasswordDialogComponent, ClusterResetUserPasswordDialogData
} from "./reset-password/cluster-reset-user-password-dialog.component";
import { parseVersionString, supportsUserPasswordReset } from "./shared";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { StatusIcon, StatusIndicatorComponent } from "../../../framework/status-indicator/status-indicator.component";
import { ClusterRestoreDialogComponent, ClusterRestoreDialogData } from "../restore/cluster-restore-dialog.component";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { idPattern, idPatternErrorText } from "typedb-web-common/lib";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { ClusterRenameDialogComponent, ClusterRenameDialogData } from "../rename/cluster-rename-dialog.component";
import { burstableTooltip, getPaymentOptions } from "../../../concept/cluster-deployment-options";
import { User } from "../../../concept/user";
import { PartialWithId } from "../../../concept/base";
import { TeamApi } from "../../../service/team/team-api.service";
import { PaymentDetailsModalComponent } from "../../team/payment-details-modal/payment-details-modal.component";

@Component({
    selector: "tp-cluster-details-page",
    templateUrl: "./cluster-details-page.component.html",
    styleUrls: ["./cluster-details-page.component.scss"],
    imports: [
        PageScaffoldComponent, ButtonComponent, AsyncPipe, RouterLink,
        DatetimePipe, StatusIndicatorComponent, TableCopyableTextCellComponent, ServerStatusPipe, MatTableModule,
        RegionPipe, MachineTypePipe, StorageSizePipe, ClusterStatusPipe, MatTooltipModule,
        MatExpansionModule, MatMenuModule, MatProgressBarModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule,
        MatPaginatorModule, DatePipe, SpinnerComponent
    ]
})
export class ClusterDetailsPageComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly team = this.app.requireCurrentTeam();
    readonly cluster$ = new BehaviorSubject<Cluster | null>(null);
    readonly spaceAccess$ = this.cluster$.pipe(
        switchMap(cluster => {
            if (cluster) return this.spaceApi.getAccessLevels([cluster.space.uuid]).pipe(map(x => x[0]));
            else return of("none" as const);
        }),
        shareReplay(1),
    );
    readonly hasWriteAccess$ = this.spaceAccess$.pipe(map(x => hasWriteAccess(x)));
    readonly hasAdminAccess$ = this.spaceAccess$.pipe(map(x => hasAdminAccess(x)));
    readonly servers$ = new ReplaySubject<Server[]>(1);
    readonly serversReady$ = new BehaviorSubject(false);
    readonly runtime$ = this.servers$.pipe(
        map((servers) => {
            const totalServers = servers.length;
            const availableServerCount = servers.filter(x => x.status === "running").length;
            return { totalServers, availableServerCount };
        }),
    );
    readonly availabilityText$ = combineLatest([this.cluster$, this.runtime$]).pipe(
        map(([cluster, runtime]) => {
            if (!cluster) return ``;
            const meaningfulStatuses: ClusterStatus[] = ["starting", "running", "resuming"];
            if (meaningfulStatuses.includes(cluster.status)) {
                return `${runtime.availableServerCount} / ${runtime.totalServers}`;
            } else return new ClusterStatusPipe().transform(cluster.status);
        }),
    );
    readonly isInPausableState$ = this.cluster$.pipe(map((cluster) => cluster ? isInPausableState(cluster) : false));
    readonly isInResumableState$ = this.cluster$.pipe(map((cluster) => cluster ? isInResumableState(cluster) : false));
    readonly cannotPauseReason$ = combineLatest([this.cluster$, this.hasWriteAccess$]).pipe(map(([cluster, canWrite]) => {
        if (!cluster) return LOADING;
        if (!isInPausableState(cluster)) return "Cluster is not running";
        if (!canWrite) return `You don't have write access to the space '${cluster.space.id}'`;
        return null;
    }), startWith(LOADING));
    readonly cannotResumeReason$ = combineLatest([this.cluster$, this.hasWriteAccess$]).pipe(map(([cluster, canWrite]) => {
        if (!cluster) return LOADING;
        if (!isInResumableState(cluster)) return "Cluster is not paused";
        if (!canWrite) return `You don't have write access to the space '${cluster.space.id}'`;
        return null;
    }), startWith(LOADING));
    readonly cannotEditReason$ = combineLatest([this.cluster$, this.hasWriteAccess$]).pipe(map(([cluster, canWrite]) => {
        if (!cluster) return LOADING;
        if (!canWrite) return `You don't have write access to the space '${cluster.space.id}'`;
        return null;
    }), startWith(LOADING));
    readonly cannotDestroyReason$ = combineLatest([this.cluster$, this.hasAdminAccess$]).pipe(map(([cluster, isAdmin]) => {
        if (!cluster) return LOADING;
        if (!isInDestroyableState(cluster)) return "Cluster is not in a destroyable state";
        if (!isAdmin) return `You don't have admin access to the space '${cluster.space.id}'`;
        return null;
    }), startWith(LOADING));
    readonly cannotConnectReason$ = this.cluster$.pipe(map((cluster) => {
        if (!cluster) return LOADING;
        else if (!isInConnectableState(cluster)) return "Cannot connect to a non-running cluster"
        return null;
    }));
    readonly cannotCloneReason$ = this.cluster$.pipe(map((cluster) => {
        if (!cluster) return LOADING;
        else if (cluster.status === "destroying" || cluster.status === "destroyed") return "Cannot clone a destroyed cluster";
        return null;
    }));
    readonly cannotResetUserPasswordReason$ = combineLatest([this.cluster$, this.hasAdminAccess$]).pipe(map(([cluster, isAdmin]) => {
        if (!cluster) return LOADING;
        if (!supportsUserPasswordReset(parseVersionString(cluster.typeDBVersion.version))) return "User password reset requires TypeDB 3.12 or later";
        if (cluster.status !== "running") return "Cluster is not running";
        if (!isAdmin) return `You don't have admin access to the space '${cluster.space.id}'`;
        return null;
    }), startWith(LOADING));
    readonly pauseEnabled$ = this.cannotPauseReason$.pipe(map(x => !x));
    readonly resumeEnabled$ = this.cannotResumeReason$.pipe(map(x => !x));
    readonly destroyEnabled$ = this.cannotDestroyReason$.pipe(map(x => !x));
    readonly editEnabled$ = this.cannotEditReason$.pipe(map(x => !x));
    readonly connectEnabled$ = this.cannotConnectReason$.pipe(map(x => !x));
    readonly cloneEnabled$ = this.cannotCloneReason$.pipe(map(x => !x));
    readonly resetUserPasswordEnabled$ = this.cannotResetUserPasswordReason$.pipe(map(x => !x));
    readonly latestCompatibleTypeDB$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        switchMap(cluster => this.clusterApi.getLatestCompatibleTypeDB(cluster!.typeDBVersion.version)),
        shareReplay(1)
    );
    readonly latestCompatibleTypeDBVersion$ = this.latestCompatibleTypeDB$.pipe(map(latest => latest.version));
    readonly typeDBUpgradeRequiresMigration$ = this.latestCompatibleTypeDB$.pipe(map(latest => latest.migrationRequired));
    readonly migrationWarning = "This update requires migrating your cluster's data. Your cluster will be unavailable until the migration completes. If you have a large data size, this can take a few hours.";
    readonly typeDBUpgradeAvailable$ = combineLatest([this.cluster$, this.latestCompatibleTypeDBVersion$]).pipe(
        map(([cluster, latestVersion]) => {
            return cluster ? cluster.typeDBVersion.version !== latestVersion : false
        })
    );
    parsedVersion$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        map(cluster => parseVersionString(cluster!.typeDBVersion.version))
    );
    isTypeDBVersion3_2OrLater$ = this.parsedVersion$.pipe(map(version => version.major >= 3 && version.minor >= 2));
    isTypeDBVersion3_2$ = this.parsedVersion$.pipe(map(version => version.major == 3 && version.minor == 2));
    studioDisabledReason$ = combineLatest([this.isTypeDBVersion3_2$, this.cannotConnectReason$]).pipe(
        map(([isTypeDBVersion3_2, cannotConnectReason]) => {
            if (isTypeDBVersion3_2) return "TypeDB Studio is not available for TypeDB 3.2.0. Please upgrade to use it."
            else return cannotConnectReason
        })
    );

    snapshotsList$ = new ReplaySubject<Snapshot[]>(1);
    snapshotsData = new MatTableDataSource<Snapshot>([]);
    @ViewChild('snapshotsPaginator', { read: MatPaginator}) snapshotsPaginator!: MatPaginator;

    clusterIsFree$ = this.cluster$.pipe(filter(cluster => !!cluster), map(cluster => cluster!.isFree));
    clusterSupportsSnapshots$ = this.cluster$.pipe(filter(cluster => !!cluster), map(cluster => cluster!.storageType.features.includes("snapshot")));
    clusterCreatedByUser$: Observable<PartialWithId<User> | undefined> = this.cluster$.pipe(filter(cluster => !!cluster?.createdByUser?.id), map(cluster => ({
        ...cluster!.createdByUser,
        id: cluster!.createdByUser!.id!
    })));
    clusterCreatedByAPIToken$ = this.cluster$.pipe(filter(cluster => !!cluster), map(cluster => cluster!.createdByAPIToken));

    readonly serverColumns = ["address", "serverStatus", "status"];
    readonly snapshotColumns = ["createdAt", "type", "actions"];

    private readonly unsub$ = new Subject<void>();
    readonly availability$: Observable<ResourceAvailability> = this.cluster$.pipe(map(x => x ? "ready" : "loading"), catchError(() => of("failed" as const)));

    readonly storageUsages$ = new ReplaySubject<number[]>(1);
    readonly serverStorageUsages$ = combineLatest([
        this.storageUsages$, this.cluster$
    ]).pipe(
        filter(([_storageUsage, cluster]) => !!cluster),
        map(([storageUsages, cluster]) =>
            storageUsages.map((storageUsage) =>
                ({
                    storageUsage: this.bytesToString(storageUsage),
                    storageUsagePercent: (storageUsage/(cluster!.storageGB * 1e7)).toFixed(2),
                    storageCapacity: `${cluster!.storageGB} GB`
                })
            )
        ),
    );
    readonly serverAddresses$ = combineLatest([this.servers$, this.cluster$]).pipe(
        filter(([servers, cluster]) => servers.every((server) => !!server.address) && servers.length == cluster?.serverCount),
        map(([servers, _]) => servers.map((server) => server.address!)),
        shareReplay(1),
    );
    readonly ready$ = this.cluster$.pipe(map((cluster) => cluster?.status == "running"));
    readonly readyStudioLink$ = combineLatest([this.isTypeDBVersion3_2OrLater$, this.ready$, this.serverAddresses$, this.cluster$]).pipe(
        map(([isTypeDBVersion3_2OrLater, ready, addresses, cluster]) => {
            if (isTypeDBVersion3_2OrLater && ready && cluster) return `https://studio.typedb.com/connect?username=admin&address=${addresses[0]}&name=${cluster.id}`;
            else return undefined;
        }),
    );
    readonly idUpdateForm = this.formBuilder.nonNullable.group({
        id: ["", [patternValidator(idPattern, idPatternErrorText), requiredValidator]]
    });
    readonly clusterAutoPauseInformation$: BehaviorSubject<ClusterAutoPauseInformation | null> =
        new BehaviorSubject<ClusterAutoPauseInformation | null>(null);
    markActiveSubmitting$ = new Subject<boolean>();
    clusterStarting$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        map(cluster => cluster!.status === "starting")
    );

    clusterPaused$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        map(cluster => cluster!.status === "paused")
    );
    displayClusterPausedNotice$ = combineLatest([
        this.clusterPaused$, this.clusterAutoPauseInformation$
    ]).pipe(
        map(([paused, autoPauseInfo]) => {
            if (!autoPauseInfo) return paused
            else return paused && !autoPauseInfo.autoPaused && !autoPauseInfo.pauseDueAt
        })
    )

    dialogOpen = false;
    password: string | undefined = undefined;
    typeDBVersionUpdateTriggered = false;

    readonly existingClusters$ = this.cluster$.pipe(
        filter(cluster => !!cluster),
        switchMap((cluster) => this.spaceApi.listClusters(
                cluster.space.uuid,
                { pagination: { offset: 0, limit: 1000 }, sorting: { attributeType: "id" } },
                of(),
            ).pipe(
                first(clusters => clusters.initial != undefined),
                map(clusters => clusters.initial!)
            )
        )
    );

    constructor(
        private app: ApplicationState, private route: ActivatedRoute, private clusterApi: ClusterApi,
        private router: Router, private snackbar: SnackbarService, private dialog: MatDialog,
        private spaceApi: SpaceApi, private teamApi: TeamApi, private destroyRef: DestroyRef,
        private resourceService: ResourceService, private formBuilder: FormBuilder,
    ) {
        this.poll((cluster) => this.clusterApi.listServers(cluster.uuid), [], 5000, (servers) => {
            this.servers$.next(servers);
            this.serversReady$.next(true);
        });
        this.poll((cluster) => this.clusterApi.listSnapshots(cluster.uuid), [], 5000, (snapshots) => {
            this.snapshotsList$.next(snapshots);
        });
        this.poll((cluster) => this.clusterApi.getStorageUsage(cluster.uuid), [], 5000, (storageUsage) => {
            this.storageUsages$.next(storageUsage);
        });
        this.poll((cluster) => {
            if (cluster.isFree) return this.clusterApi.checkClusterAutoPause(cluster.uuid);
            else return of(null);
        }, null, 60_000, (clusterAutoPauseInformation) => {
            if (clusterAutoPauseInformation) this.clusterAutoPauseInformation$.next(clusterAutoPauseInformation);
        });

        this.cluster$.pipe(first(cluster => !!cluster)).subscribe(cluster => this.idUpdateForm.patchValue({id: cluster!.id}));

        combineLatest([this.cluster$, this.route.queryParamMap]).pipe(
            filter(([cluster, params]) => params.get(DIALOG) === SETUP && cluster?.isAdminUserInitialized === false),
            first(),
        ).subscribe(() => {
            this.openSetupDialog(true);
        });

        this.snapshotsList$.subscribe(snapshots => {
            this.snapshotsData.data = snapshots
            this.snapshotsData.paginator = this.snapshotsPaginator
        });
    }

    openSetupDialog(disableClose: boolean) {
        this.dialogOpen = true;
        this.dialog.open(ClusterSetupDialogComponent, {
            data: {
                cluster$: this.cluster$.pipe(
                    filter(cluster => !!cluster),
                    map(cluster => cluster!),
                ),
                servers$: this.servers$,
                password: this.password,
                updatePassword: (newPassword: string | undefined) => this.password = newPassword,
            },
            width: "720px",
            maxWidth: "100%",
            maxHeight: "100vh",
            disableClose,
        }).beforeClosed().subscribe(() => this.dialogOpen = false);
    }

    openConsoleConnectDialog() {
        this.cluster$.pipe(first(cluster => !!cluster), map(cluster => cluster!)).subscribe((cluster) => {
            if (!cluster.isAdminUserInitialized) return this.openSetupDialog(false);
            this.dialogOpen = true;
            this.dialog.open(ClusterConsoleConnectDialogComponent, {
                data: {
                    cluster$: this.cluster$.pipe(
                        filter(cluster => !!cluster),
                        map(cluster => cluster!),
                    ),
                    servers$: this.servers$,
                },
                width: "700px",
                maxWidth: "100%",
                maxHeight: "100vh",
            }).beforeClosed().subscribe(() => this.dialogOpen = false);
        });
    }

    openDriversConnectDialog() {
        this.cluster$.pipe(first(cluster => !!cluster), map(cluster => cluster!)).subscribe((cluster) => {
            if (!cluster.isAdminUserInitialized) return this.openSetupDialog(false);
            this.dialogOpen = true;
            this.dialog.open(ClusterDriversConnectDialogComponent, {
                data: {
                    cluster$: this.cluster$.pipe(
                        filter(cluster => !!cluster),
                        map(cluster => cluster!),
                    ),
                    servers$: this.servers$,
                },
                width: "700px",
                maxWidth: "100%",
                maxHeight: "100vh",
            }).beforeClosed().subscribe(() => this.dialogOpen = false);
        });
    }

    openHttpConnectDialog() {
        this.cluster$.pipe(first(cluster => !!cluster), map(cluster => cluster!)).subscribe((cluster) => {
            if (!cluster.isAdminUserInitialized) return this.openSetupDialog(false);
            this.dialogOpen = true;
            this.dialog.open(ClusterHttpConnectDialogComponent, {
                data: {
                    cluster$: this.cluster$.pipe(
                        filter(cluster => !!cluster),
                        map(cluster => cluster!),
                    ),
                    servers$: this.servers$,
                },
                width: "700px",
                maxWidth: "100%",
                maxHeight: "100vh",
            }).beforeClosed().subscribe(() => this.dialogOpen = false);
        });
    }

    openStudioConnectDialog() {
        this.cluster$.pipe(first(cluster => !!cluster), map(cluster => cluster!)).subscribe((cluster) => {
            if (!cluster.isAdminUserInitialized) return this.openSetupDialog(false);
            this.dialogOpen = true;
            this.dialog.open(ClusterStudioConnectDialogComponent, {
                data: {
                    cluster$: this.cluster$.pipe(
                        filter(cluster => !!cluster),
                        map(cluster => cluster!),
                    ),
                    servers$: this.servers$,
                },
                width: "700px",
                maxWidth: "100%",
                maxHeight: "100vh",
            }).beforeClosed().subscribe(() => this.dialogOpen = false);
        });
    }

    ngOnInit() {
        this.initDataLoader();
    }

    ngAfterViewInit() {
        this.snapshotsData.paginator = this.snapshotsPaginator;
    }

    ngOnDestroy() {
        this.unsub$.next();
    }

    initDataLoader() {
        this.route.params.pipe(
            filter((params) => params["space-id"] && params["cluster-id"]),
            map((params) => ({ spaceId: params["space-id"] as string, clusterId: params["cluster-id"] as string })),
            distinctUntilChanged((a, b) => a.clusterId === b.clusterId && a.spaceId === b.spaceId),
            tap(() => { this.unsub$.next(); }),
            switchMap(({ spaceId, clusterId }) => {
                return this.spaceApi.getSpace({ spaceId, teamUuid: this.team.uuid }, of(undefined)).pipe(
                    first(),
                    map((space) => ({ space: space.initial!, clusterId })),
                );
            }),
            switchMap(({ space, clusterId }) => {
                return this.clusterApi.getCluster({ clusterId: clusterId, spaceUuid: space.uuid }, this.unsub$);
            }),
        ).subscribe({
            next: (res) => {
                if (res.update?.typeDBVersion && this.typeDBVersionUpdateTriggered) this.snackbar.success("TypeDB version successfully updated");
                this.resourceService.processResponse({ resource$: this.cluster$, res: res, unsub$: this.unsub$, onResync: () => this.initDataLoader() });
            },
            error: (err) => {
                this.cluster$.error(err);
            }
        });
    }

    openEditModal() {
        this.storageUsages$.pipe(first(x => !!x.length)).subscribe((storageUsages => {
            this.dialog.open<ClusterUpdateDialogComponent, ClusterEditDialogData, DialogResult>(ClusterUpdateDialogComponent, {
                data: { cluster: this.cluster$.value!, currentStorageUsage: storageUsages[0]  },
                panelClass: "update-cluster",
                width: "1100px",
            }).beforeClosed().subscribe((result) => {
                if (result === "ok") this.initDataLoader();
            });
        }));
    }

    openResetUserPasswordDialog() {
        this.dialogOpen = true;
        this.dialog.open<ClusterResetUserPasswordDialogComponent, ClusterResetUserPasswordDialogData, DialogResult>(
            ClusterResetUserPasswordDialogComponent, { data: { cluster: this.cluster$.value!, serverAddresses$: this.serverAddresses$ } }
        ).beforeClosed().subscribe(() => this.dialogOpen = false);
    }

    openRenameModal() {
        this.dialog.open<ClusterRenameDialogComponent, ClusterRenameDialogData, DialogResult>(ClusterRenameDialogComponent, {
            data: { cluster: this.cluster$.value! }
        }).beforeClosed().subscribe((result) => {
            if (result === "ok") this.initDataLoader();
        });
    }

    openPauseResumeModal(action: "resume" | "pause") {
        const cluster = this.cluster$.value!;
        const modal = this.dialog.open<PauseResumeModalComponent, PauseResumeModalData>(PauseResumeModalComponent, {
            data: { cluster, action },
        }).componentInstance;
        modal.confirmed.pipe(
            switchMap(() => {
                switch (action) {
                    case "pause": return this.clusterApi.pauseCluster(cluster.uuid);
                    case "resume": return this.clusterApi.resumeCluster(cluster.uuid);
                }
            }),
        ).subscribe({
            next: () => {
                modal.close();
                switch (action) {
                    case "pause": this.snackbar.success(`'${cluster.id}' is now being paused.`); break;
                    case "resume": this.snackbar.success(`'${cluster.id}' is now being resumed.`); break;
                }
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    openDestroyModal() {
        const cluster = this.cluster$.value!;
        const modal = this.dialog.open<StrongConfirmationModalComponent, StrongConfirmationModalData>(StrongConfirmationModalComponent, {
            data: {
                title: "Destroy cluster",
                body: `Are you sure you would like to destroy '${cluster.id}'? All of its data and backups will be permanently erased. This action cannot be undone!`,
                confirmText: "Destroy cluster",
                strongConfirmationString: cluster.id,
            },
        }).componentInstance;
        modal.confirmed.pipe(switchMap(() => this.clusterApi.destroyCluster(cluster.uuid))).subscribe({
            next: () => {
                modal.close();
                this.snackbar.success(`'${cluster.id}' is now being destroyed.`);
                this.router.navigate([clustersPath(this.team)]);
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    openUpdateTypeDBModal() {
        this.latestCompatibleTypeDB$.pipe(first()).subscribe(({ version: latestCompatibleTypeDBVersion, migrationRequired }) => {
            const cluster = this.cluster$.value!;
            const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
                data: {
                    title: "Update TypeDB",
                    body: `Are you sure you would like to update '${cluster.id}' to TypeDB ${latestCompatibleTypeDBVersion}?`,
                    warning: migrationRequired ? this.migrationWarning : undefined,
                    confirmText: `Update to ${latestCompatibleTypeDBVersion}`,
                    confirmButtonStyle: "primary-outline green stroke",
                },
            }).componentInstance;
            modal.confirmed.pipe(
                tap(() => this.typeDBVersionUpdateTriggered = true),
                switchMap(() => this.clusterApi.updateToLatestCompatibleTypeDB(cluster.uuid)),
            ).subscribe({
                next: () => {
                    modal.close();
                    this.snackbar.success(`'${cluster.id}' is now being updated to TypeDB ${latestCompatibleTypeDBVersion}.`);
                },
                error: () => {
                    modal.isSubmitting$.next(false);
                },
            });
        });
    }

    serverIcon(server: Server, dialogOpen: boolean): StatusIcon {
        switch (server.status) {
            case "running":
                return "ok";
            case "pending":
                if (dialogOpen) return "loading_paused"
                else return "loading"
            case "unknown":
            case "error":
                return "error";
        }
    }

    handleServerTableRowClick(server: Server) {
        if (!isBlank(window.getSelection()?.toString())) return; // handle case where user wants to select the address
        this.router.navigate([serverDetailsPath(server, this.cluster$.value!, this.team)]);
    }

    clusterIcon(cluster: Cluster): StatusIcon {
        switch (cluster.status) {
            case "running":
                return "ok";

            case "starting":
            case "resuming":
            case "restoring":
            case "pausing":
            case "destroying":
            case "updatingTypeDB":
            case "reconfiguring":
            case "migrating":
                return"loading";

            case "paused":
            case "destroyed":
                return "inactive";
            case "error":
                return "error";
        }
    }

    clusterStatusTooltip(cluster: Cluster): string | undefined {
        if (cluster.status == "error") return "Your cluster has encountered an error - please contact support.";
        else if (cluster.status == "migrating") return "Your cluster's data is being migrated to the new version - this may take a while.";
        else return undefined;
    }

    bytesToString(bytes: number) {
        if (bytes === 0) return null
        else if (bytes < 1e3) return `${bytes} B`
        else if (bytes < 1e6) return `${(bytes/1e3).toFixed(2)} kB`
        else if (bytes < 1e9) return `${(bytes/1e6).toFixed(2)} MB`
        else return `${(bytes/1e9).toFixed(2)} GB`
    }

    takeSnapshot() {
        const cluster = this.cluster$.value!;
        const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
            data: {
                title: "Take snapshot",
                body: `Would you like to take a manual snapshot for '${cluster.id}'? Manual snapshots will be retained until manually deleted.`,
                confirmText: `Take snapshot`,
                confirmButtonStyle: "primary-outline green stroke",
            },
        }).componentInstance;
        modal.confirmed.pipe(
            switchMap(() => this.clusterApi.takeSnapshot(cluster!.uuid)),
            switchMap(() => this.clusterApi.listSnapshots(cluster.uuid)),
        ).subscribe({
            next: (updatedSnapshots) => {
                this.snapshotsList$.next(updatedSnapshots);
                modal.close();
                this.snackbar.success(`Snapshot for '${cluster.id}' is now being taken.`);
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    restoreCluster(snapshot: Snapshot) {
        this.dialog.open<ClusterRestoreDialogComponent, ClusterRestoreDialogData>(ClusterRestoreDialogComponent, { data: { cluster: this.cluster$.value!, snapshot } });
    }

    deleteSnapshot(snapshot: Snapshot) {
        const cluster = this.cluster$.value!;
        const snapshotTimeString = formatDate(snapshot.createdAt, "d MMM yyyy, HH:mm:ss", "en-US");
        const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
            data: {
                title: "Delete snapshot",
                body: `Would you like to delete the snapshot taken at ${snapshotTimeString}? Any data stored in it will be lost permanently.`,
                confirmText: `Delete snapshot`,
                confirmButtonStyle: "primary-outline red stroke",
            },
        }).componentInstance;
        modal.confirmed.pipe(
            switchMap(() => this.clusterApi.deleteSnapshot({ clusterUuid: cluster!.uuid, snapshot: snapshot.id })),
            switchMap(() => this.clusterApi.listSnapshots(cluster.uuid)),
        ).subscribe({
            next: (updatedSnapshots) => {
                this.snapshotsList$.next(updatedSnapshots);
                modal.close();
                this.snackbar.success(`Snapshot taken at ${snapshotTimeString} is now being deleted.`);
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    trackServerByUUID(index: number, item: Server) {
        return item.uuid;
    }

    trackSnapshotByID(index: number, item: Snapshot) {
        return item.id;
    }

    markClusterAsActive() {
        this.markActiveSubmitting$.next(true);
        const clusterUuid = this.cluster$.value!.uuid;
        this.clusterApi.markClusterAsActive(clusterUuid).subscribe({
            next: ()  => {
                this.markActiveSubmitting$.next(false);
                this.clusterApi.checkClusterAutoPause(clusterUuid).pipe(first()).subscribe(clusterAutoPauseInformation => {
                    if (clusterAutoPauseInformation) this.clusterAutoPauseInformation$.next(clusterAutoPauseInformation);
                });
            },
            error: () => {
                this.markActiveSubmitting$.next(false);
            }
        });
    }

    private poll<T>(request: (cluster: Cluster) => Observable<T>, start: T, pollInterval: number, onNext: (value: T) => void) {
        this.cluster$.pipe(
            takeUntilDestroyed(),
            filter(cluster => !!cluster),
            map(cluster => cluster!),
            tap(cluster => (window as any)["cluster"] = cluster),
            switchMap((cluster) => interval(pollInterval).pipe(
                takeUntilDestroyed(this.destroyRef),
                startWith(-1),
                switchMap(() => request(cluster))
            )),
            startWith(start),
        ).subscribe(onNext);
    }

    cloneCluster() {
        const cluster = this.cluster$.value!;
        this.teamApi.getPayableClusterDeploymentOptions(this.team.uuid).subscribe((payableProviders) => {
            if (getPaymentOptions(payableProviders, cluster.provider.id, cluster.region.id, cluster.machineType.vendorId).length > 0) {
                this.openCloneClusterModal(cluster);
            } else {
                this.openCloneClusterPaymentAddDialog(cluster);
            }
        });
    }

    openCloneClusterModal(cluster: Cluster) {
        const modal = this.dialog.open<CloneClusterModalComponent, CloneClusterModalData>(CloneClusterModalComponent, {
            data: { cluster },
        }).componentInstance;
        modal.confirmed.pipe(
            switchMap(() => this.existingClusters$),
            switchMap((existingClusters) =>
                generateUniqueId(`${cluster.id}-clone`, 0, (id) => of(existingClusters.some(cluster => cluster.id === id)))
            ),
            first(),
            switchMap((id) => this.clusterApi.cloneCluster(cluster.uuid, id).pipe(map(() => id))),
        ).subscribe({
            next: (id) => {
                this.snackbar.success(`Cloning '${cluster.id}' to '${id}'.`);
                modal.close();
                this.router.navigate(["..", id], { relativeTo: this.route });
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    openCloneClusterPaymentAddDialog(cluster: Cluster) {
        const modal = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
            data: {
                title: `Clone ${cluster.id}`,
                body: `To clone ${cluster.id}, please ask a team administrator to add a payment method, then refresh to continue.`,
                confirmText: `Add payment method`,
                confirmButtonStyle: "primary-outline green stroke",
            },
        }).componentInstance;
        modal.confirmed.subscribe({
            next: () => {
                this.openPaymentDetailsDialog();
                modal.close();
            },
            error: () => {
                modal.isSubmitting$.next(false);
            },
        });
    }

    openPaymentDetailsDialog() {
        this.dialog.open<PaymentDetailsModalComponent, any, DialogResult>(PaymentDetailsModalComponent).afterClosed().subscribe(result => {
            if (result === "ok") {
                this.snackbar.success("Payment method added successfully");
            }
            else if (result == null || result === "cancelled") return;
            else this.snackbar.errorPersistent(`Error setting up payment method: ${result.error}`);
        });
    }

    protected readonly toSentenceCase = toSentenceCase;
    protected readonly burstableTooltip = burstableTooltip;
    protected readonly memberDetailsPath = memberDetailsPath;
    protected readonly teamSettingsPath = teamSettingsPath;
}
